// TERM8-F1 (docs/build-briefs/TERM8-F1-reader-tip-settings-screen.md) and TIPONE1 (DEC-541). The words of Settings, Tabs and
// payment, Card reader tips. There is one tip setting for the tablet and the reader, so this screen holds the switch, the
// cart pause and a line that says which tip choices the reader is using. Pure functions, no DOM, so they are tested directly.

import type {
  ReaderTipProviderStateDto,
  ReaderTipSettingsDto,
  ReaderTipSettingsPutBody,
} from "./api.ts";

export interface ReaderTipDraft {
  enabled: boolean;
}

export function draftFromView(view: Pick<ReaderTipSettingsDto, "enabled">): ReaderTipDraft {
  return { enabled: view.enabled };
}

/** 2000 -> "20%", 2250 -> "22.50%". Whole percents read without decimals. */
export function percentText(basisPoints: number): string {
  return basisPoints % 100 === 0
    ? `${basisPoints / 100}%`
    : `${Math.trunc(basisPoints / 100)}.${String(basisPoints % 100).padStart(2, "0")}%`;
}

/** "Tip choices: 20%, 22%, 25%". The one tip setting, low to high. */
export function tipChoicesLine(basisPoints: readonly number[]): string {
  if (basisPoints.length === 0) return "Tip choices: none yet";
  return `Tip choices: ${[...basisPoints].sort((a, b) => a - b).map(percentText).join(", ")}`;
}

export const TIP_CHOICES_NOTE = "Guests see these on the tablet Pay screen and the card reader.";

/** What a Save sends: the switch. */
export function buildPutBody(draft: ReaderTipDraft): ReaderTipSettingsPutBody {
  return { enabled: draft.enabled };
}

export function draftIsUnchanged(draft: ReaderTipDraft, view: ReaderTipSettingsDto): boolean {
  return draftFromView(view).enabled === draft.enabled;
}

export interface ProviderStatePill {
  label: string;
  tone: "good" | "warn" | "neutral";
}

/** Labels and states only. */
export function providerStatePill(state: ReaderTipProviderStateDto): ProviderStatePill {
  switch (state) {
    case "applied":
      return { label: "Sent to the reader", tone: "good" };
    case "pending":
      return { label: "Not sent yet", tone: "warn" };
    case "not_registered":
      return { label: "No reader yet", tone: "neutral" };
    case "simulated":
      return { label: "Test reader", tone: "neutral" };
  }
}

/** TABSPAY1: the sentence beside the chip, so the chip is never a bare word. */
export function providerStateNote(state: ReaderTipProviderStateDto): string {
  switch (state) {
    case "applied":
      return "The reader has the current tip settings.";
    case "pending":
      return "The reader has not received the latest tip settings yet.";
    case "not_registered":
      return "No card reader is set up at this location yet. Add one under Devices.";
    case "simulated":
      return "This location is using test payments, so no real reader receives these settings.";
  }
}

export const SAVED_LINE = "Saved.";
export const SAVED_TOAST = "Card reader settings saved.";
export const SECTION_TITLE = "Card reader";
export const SECTION_HELP =
  "Applies when a guest pays by card on the reader. It affects what guests see on the reader before and during payment.";
export const LOCATION_PICKER_LABEL = "Settings for";
export const SAVE_LABEL = "Save card reader settings";
export const PAUSE_LABEL = "Show drinks on the reader before payment";
export const PAUSE_NOTE = "The reader lists the drinks first, then moves on to the tip and the card by itself. One tap does it all.";
export const TIP_CHOICES_LINK = "Change in Money and pricing";
export const REFRESH_DELAY_LINE = "The reader can take up to 10 minutes to show a change.";
export const SWITCH_LABEL = "Card reader asks for a tip";

/** Pause writes leave tip choices and provider synchronization independent. */
export function cartPausePutBody(enabled: boolean): ReaderTipSettingsPutBody {
  return { cartPauseEnabled: enabled };
}
